import type React from "react"
import { useState, useEffect } from "react"
import { Form, Radio, Space } from "antd"
import ButtonLink from "components/theme_components/ButtonLink"
import { CheckOutlined } from "@ant-design/icons"
import FormInput from "components/FormInput"

type ExportModalProps = {
  setVisible: (visible: boolean) => void
  data: any[]
  filename: string
  columns: string[]
  columnLabels: Record<string, string>
}

const ExportModal: React.FC<ExportModalProps> = ({ setVisible, data, filename, columns, columnLabels }) => {
  const [form] = Form.useForm()
  const [exportFormat, setExportFormat] = useState<"xls" | "csv">("xls")
  const [isLibraryLoaded, setIsLibraryLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Load the SheetJS library from CDN for XLS export
  useEffect(() => {
    if (!window.XLSX) {
      const script = document.createElement("script")
      script.src = "https://cdn.sheetjs.com/xlsx-0.19.3/package/dist/xlsx.full.min.js"
      script.async = true
      script.onload = () => setIsLibraryLoaded(true)
      document.body.appendChild(script)

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script)
        }
      }
    } else {
      setIsLibraryLoaded(true)
    }
  }, [])

  // Helper function to check if a value is a date
  const isDate = (value: any): boolean => {
    return value instanceof Date && !isNaN(value.getTime())
  }

  // Helper function to format dates
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0")
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  // Process data for export (common for both formats)
  const processData = () => {
    if (!data || !data.length) return []

    // Determine which columns to include
    let columnsToExport: string[] = []
    if (columns && columns.length > 0) {
      columnsToExport = columns.filter((col) => data[0] && col in data[0])
    } else {
      columnsToExport = Object.keys(data[0] || {})
    }

    // Process data to handle dates and special values
    const processedData = data.map((item) => {
      const newItem: Record<string, any> = {}

      columnsToExport.forEach((key) => {
        if (item[key] === null || item[key] === undefined) {
          newItem[key] = ""
        } else if (isDate(item[key])) {
          newItem[key] = formatDate(item[key])
        } else if (typeof item[key] === "number" && item[key] > 10000000000 && item[key] < 100000000000000) {
          const date = new Date(item[key])
          if (!isNaN(date.getTime())) {
            newItem[key] = formatDate(date)
          } else {
            newItem[key] = item[key]
          }
        } else if (typeof item[key] === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(item[key])) {
          const date = new Date(item[key])
          if (!isNaN(date.getTime())) {
            newItem[key] = formatDate(date)
          } else {
            newItem[key] = item[key]
          }
        } else {
          newItem[key] = item[key]
        }
      })

      return newItem
    })

    // Apply column labels if provided
    if (Object.keys(columnLabels).length > 0) {
      return processedData.map((row) => {
        const newRow: Record<string, any> = {}
        columnsToExport.forEach((key) => {
          const newKey = columnLabels[key] || key
          newRow[newKey] = row[key]
        })
        return newRow
      })
    }

    return processedData
  }

  const handleExportXLS = async () => {
    if (!isLibraryLoaded) {
      console.warn("Excel library not loaded yet")
      return
    }

    try {
      setIsLoading(true)
      const XLSX = window.XLSX
      const exportData = processData()

      if (!exportData.length) {
        console.warn("No data to export")
        return
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)

      // Get headers and calculate column widths
      const headers = Object.keys(exportData[0] || {})
      const columnWidths = headers.map((header) => {
        let maxWidth = header.length + 2
        exportData.forEach((row) => {
          const cellValue = String(row[header] || "")
          const lines = cellValue.split("\n")
          lines.forEach((line) => {
            let contentLength = line.length
            if (typeof row[header] === "number") {
              contentLength += 1
            }
            maxWidth = Math.max(maxWidth, contentLength + 2)
          })
        })
        return Math.min(maxWidth, 50)
      })

      // Set column widths
      ws["!cols"] = columnWidths.map((width) => ({ wch: width }))

      // Apply formatting
      if (!ws["!rows"]) ws["!rows"] = []
      ws["!rows"][0] = { hpt: 24 }

      // Format header cells
      for (let i = 0; i < headers.length; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: i })
        if (!ws[cellRef]) continue
        ws[cellRef].s = {
          font: { bold: true, sz: 12 },
          fill: { fgColor: { rgb: "EFEFEF" } },
          alignment: { wrapText: true, vertical: "center", horizontal: "center" },
        }
      }

      // Format data cells
      for (let r = 1; r < exportData.length + 1; r++) {
        for (let c = 0; c < headers.length; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c })
          if (!ws[cellRef]) continue
          ws[cellRef].s = {
            alignment: { wrapText: true, vertical: "top" },
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Sheet1")

      // Generate and download file
      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      })

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `${filename}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error exporting XLS:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportCSV = async () => {
    try {
      setIsLoading(true)
      const exportData = processData()

      if (!exportData.length) {
        console.warn("No data to export")
        return
      }

      const headers = Object.keys(exportData[0])

      // Create CSV content
      let csvContent = "sep=,\n" // CSV separator hint

      // Add headers row
      csvContent += headers.join(",") + "\n"

      // Add data rows
      exportData.forEach((item) => {
        const row = headers.map((header) => {
          const cellStr = String(item[header] || "")
          // Escape quotes and wrap in quotes if needed
          if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
            return `"${cellStr.replace(/"/g, '""')}"`
          }
          return cellStr
        })
        csvContent += row.join(",") + "\n"
      })

      // Create and download CSV file
      const encoder = new TextEncoder()
      const bom = encoder.encode("\uFEFF") // UTF-8 BOM
      const contentBytes = encoder.encode(csvContent)

      const blob = new Blob([bom, contentBytes], {
        type: "text/csv;charset=utf-8",
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `${filename}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error exporting CSV:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleValidate = async () => {
    if (exportFormat === "xls") {
      await handleExportXLS()
    } else {
      await handleExportCSV()
    }
    setVisible(false)
  }

  const handleCancel = () => {
    setVisible(false)
  }

  return (
    <Form form={form} layout="vertical" >
      <FormInput className="form-item no-border-radius ut-task-modal-title" nolabel>
        <Radio.Group value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="ut-radio-group">
          <Radio value="xls" >
            .xls
          </Radio>
          <Radio value="csv" >
            .csv
          </Radio>
        </Radio.Group>
      </FormInput>

      <div className="d-flex justify-between align-center pt-4">
        <div></div>
        <Space>
          <ButtonLink comcode="Annuler" onClick={handleCancel} className="btn-light" />
          <ButtonLink
            comcode="Valider"
            type="primary"
            onClick={handleValidate}
            className="no-border-radius"
            loading={isLoading}
            disabled={isLoading || (exportFormat === "xls" && !isLibraryLoaded)}
            icon={<CheckOutlined />}
          />
        </Space>
      </div>
    </Form>
  )
}

// Add type definition for the XLSX library on the window object
declare global {
  interface Window {
    XLSX: any
  }
}

export default ExportModal